import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import { formatGerman } from '@/src/components/core/lib/date'

export type Course = FahrlehrerCh.CourseWithLocation
export type BookableCourse = FahrlehrerCh.CourseWithLocation['course']

export const WEEKDAY_ORDER = [
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
  'Sonntag',
]

export function courseStartDate(course: BookableCourse): Date {
  const starts = course.slots.map((slot) => new Date(slot.start).getTime())
  return new Date(Math.min(...starts))
}

export type CourseDay = {
  isoDay: string
  dateText: string
  timeText: string
}

export function getCourseDays(course: BookableCourse): CourseDay[] {
  const byDay = new Map<string, { start: Date; end: Date }[]>()
  for (const slot of course.slots) {
    const start = new Date(slot.start)
    const key = formatGerman(start, 'yyyy-MM-dd')
    const existing = byDay.get(key)
    if (existing) {
      existing.push({ start, end: new Date(slot.end) })
    } else {
      byDay.set(key, [{ start, end: new Date(slot.end) }])
    }
  }

  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, slots]) => {
      const sorted = [...slots].sort(
        (a, b) => a.start.getTime() - b.start.getTime()
      )
      const timeText =
        sorted
          .map(
            (slot) =>
              `${formatGerman(slot.start, 'HH:mm')} – ${formatGerman(slot.end, 'HH:mm')}`
          )
          .join(' & ') + ' Uhr'
      return {
        isoDay: key,
        dateText: formatGerman(sorted[0].start, 'EEEE, d. MMM yyyy'),
        timeText,
      }
    })
}

export function formatAddress(address: BookableCourse['address']): string {
  return `${address.street}, ${address.zip} ${address.place}`
}

export function courseWeekdayLabel(course: BookableCourse): string {
  const days = new Set<string>()
  for (const slot of course.slots) {
    days.add(formatGerman(new Date(slot.start), 'EEEE'))
  }
  return [...days]
    .sort((a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b))
    .join(' & ')
}

export function courseMonthLabel(course: BookableCourse): string {
  return formatGerman(courseStartDate(course), 'LLLL')
}

export function getTagText(course: BookableCourse): string {
  const dayCount = getCourseDays(course).length
  switch (course.type) {
    case 'eNothelfer':
      return `${dayCount} Tag${dayCount === 1 ? '' : 'e'} (mit eLearning)`
    case 'Nothelfer':
      return `${dayCount} Tag${dayCount === 1 ? '' : 'e'}`
    default:
      return course.type
  }
}

export function getSeatText(course: BookableCourse): string | null {
  if (course.seats === 2) return '2 Plätze frei'
  if (course.seats === 1) return '1 Platz frei'
  return null
}

export const COURSE_TYPE_NAMES: Record<string, string> = {
  firstaid: 'Nothelferkurse',
  vku: 'Verkehrskundeunterricht',
  wab: 'WAB Kurse',
  pgs: 'Motorradgrundkurse',
}

const COURSE_TYPE_HEADER_TITLES: Record<string, string> = {
  firstaid: 'Nothelferkurs',
  vku: 'VKU',
  wab: 'WAB Kurs',
  pgs: 'Motorrad Grundkurs',
}

export function getCourseHeaderTitle(
  courseTypeGroup: string,
  location: string
): string {
  const name = COURSE_TYPE_HEADER_TITLES[courseTypeGroup] ?? 'Kurse'
  return `${name} ${location}`
}

export function getCourseHeaderHeading(
  courseTypeGroup: string,
  location: string
): string {
  switch (courseTypeGroup) {
    case 'firstaid':
      return `Nothelferkurse mit E-Learning in ${location}`
    case 'vku':
      return `Verkehrskundeunterricht (VKU) in ${location}`
    case 'wab':
      return `WAB Kurse in der Nähe von ${location}`
    case 'pgs':
      return `Motorrad fahren lernen in ${location}`
    default:
      return `Kurse in ${location}`
  }
}

export const COURSE_TYPE_SHORT_NAMES: Record<string, string> = {
  firstaid: 'Nothelfer',
  vku: 'VKU',
  wab: 'WAB',
  pgs: 'Motorradgrund',
}

const COURSE_TYPE_TITLES: Record<string, string> = {
  eNothelfer: 'Nothilfekurs mit eLearning',
  Nothelfer: 'Nothilfekurs',
  VKU: 'Verkehrskundeunterricht',
  WAB: 'WAB Kurs',
  PGS: 'Motorradgrundkurs',
}

export function getCourseTitle(course: BookableCourse): string {
  return COURSE_TYPE_TITLES[course.type] ?? course.type
}
